import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Badge, type BadgeTone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Switch'
import { CourtOperatingHourSaveConfirmModal } from '@/components/venue/CourtOperatingHourSaveConfirmModal'
import { useCourtOperatingHours } from '@/hooks/useCourtOperatingHours'
import { useCourts } from '@/hooks/useCourts'
import { useSaveCourtOperatingHours } from '@/hooks/useSaveCourtOperatingHours'
import { ApiError, toApiError } from '@/lib/api/errors'
import { TIME_OF_DAY_OPTIONS, type CourtOperatingHourView } from '@/lib/venue/operatingHourTypes'
import {
  applyDiffersByDayToggle,
  hasOperatingHourFormErrors,
  toOperatingHourFormState,
  toOperatingHourSaveInput,
  toReservationUnitOptions,
  validateOperatingHourForm,
  type OperatingHourFormErrors,
  type OperatingHourFormState,
} from '@/lib/venue/operatingHourViewModel'
import { useVenueContext } from '@/routes/venueContext'

/** 🚨 배지 색만 프런트가 매핑한다 — 텍스트는 서버 라벨(`statusLabel`) 그대로(`CourtCard.tsx` 규칙 승계). */
const STATUS_TONE: Record<CourtOperatingHourView['status'], BadgeTone> = {
  OPEN: 'success',
  SUSPENDED: 'neutral',
  MAINTENANCE: 'warning',
}

const EMPTY_ERRORS: OperatingHourFormErrors = { dayErrors: {} }

/**
 * 코트 운영시간 설정 (요구사항, 설계 §2). `courts/:courtId/operating-hours` 의 플레이스홀더를 대체한다.
 *
 * 🚨 저장 가능 여부(`editable`)·예약 단위 잠금(`unitChangeable`) 은 이 화면의 GET 응답 필드를 그대로
 * 바인딩한다 — 클라이언트가 재계산하지 않는다(`VenueCourtsPage` 와 같은 원칙).
 */
export function VenueCourtOperatingHoursPage() {
  const { venueId } = useVenueContext()
  const { courtId: courtIdParam } = useParams<{ courtId: string }>()
  const courtId = Number(courtIdParam)
  const navigate = useNavigate()

  const query = useCourtOperatingHours(venueId, courtId)
  const courtsQuery = useCourts(venueId)
  const saveMutation = useSaveCourtOperatingHours(venueId, courtId)

  const [form, setForm] = useState<OperatingHourFormState | null>(null)
  const [errors, setErrors] = useState<OperatingHourFormErrors>(EMPTY_ERRORS)
  const [confirming, setConfirming] = useState(false)
  const [generalError, setGeneralError] = useState<ApiError | null>(null)

  // 조회가 도착하면 폼을 한 번 초기화한다. 이후 편집 중에는 재조회로 덮어쓰지 않는다
  // (`VenueProfileEditPage.tsx` 규칙 승계).
  useEffect(() => {
    if (query.data && form === null) {
      setForm(toOperatingHourFormState(query.data))
    }
  }, [query.data, form])

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="운영시간 설정" />
        <LoadingState rows={4} label="운영시간 설정을 불러오는 중" />
      </>
    )
  }

  if (query.isError) {
    return (
      <>
        <PageHeader title="운영시간 설정" />
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      </>
    )
  }

  if (!query.data || !form) return null

  const view = query.data
  const editable = view.editable
  const unitLocked = !view.unitChangeable
  const unitOptions = toReservationUnitOptions(view)
  const courtOptions = courtsQuery.data?.courts.map((court) => ({
    courtId: court.courtId,
    name: court.name,
  })) ?? [{ courtId, name: view.courtName }]

  function updateForm(patch: Partial<OperatingHourFormState>) {
    setForm((current) => (current ? { ...current, ...patch } : current))
  }

  function updateDay(dayOfWeek: string, patch: Partial<OperatingHourFormState['days'][number]>) {
    setForm((current) =>
      current
        ? {
            ...current,
            days: current.days.map((day) =>
              day.dayOfWeek === dayOfWeek ? { ...day, ...patch } : day
            ),
          }
        : current
    )
  }

  function handleToggleDiffersByDay(next: boolean) {
    setForm((current) => (current ? applyDiffersByDayToggle(current, next) : current))
  }

  function handleSaveClick() {
    if (!form) return
    const clientErrors = validateOperatingHourForm(form)
    if (hasOperatingHourFormErrors(clientErrors)) {
      setErrors(clientErrors)
      return
    }
    setErrors(EMPTY_ERRORS)
    setGeneralError(null)
    setConfirming(true)
  }

  async function handleConfirmSave() {
    if (!form) return
    try {
      await saveMutation.mutateAsync(toOperatingHourSaveInput(form))
      setConfirming(false)
    } catch (error) {
      setConfirming(false)
      setGeneralError(toApiError(error))
    }
  }

  return (
    <>
      <PageHeader
        title="운영시간 설정"
        description="이 범위 안에서만 예약 가능 시간이 표시됩니다."
      />

      <div className="mb-5 space-y-3">
        {!editable ? (
          <Alert tone="warning">
            <p>{view.editBlockNotice}</p>
          </Alert>
        ) : null}
        {generalError ? (
          <Alert tone="danger">
            <p>{generalError.message}</p>
          </Alert>
        ) : null}
      </div>

      <Card className="mb-5">
        <CardBody className="flex flex-wrap items-center gap-4">
          <div>
            <label
              className="mb-1.5 block text-xs font-medium text-slateish-500"
              htmlFor="operating-hours-court-select"
            >
              코트 선택
            </label>
            <Select
              id="operating-hours-court-select"
              value={courtId}
              onChange={(event) => {
                navigate(`/venues/${venueId}/courts/${event.target.value}/operating-hours`)
              }}
            >
              {courtOptions.map((court) => (
                <option key={court.courtId} value={court.courtId}>
                  {court.name}
                </option>
              ))}
            </Select>
          </div>
          <Badge tone={STATUS_TONE[view.status]}>{view.statusLabel}</Badge>
        </CardBody>
      </Card>

      <div className="space-y-5">
        <Card>
          <CardHeader title="예약 단위·최대 예약 시간" />
          <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="operating-hours-unit"
              >
                예약 가능 시간 단위
              </label>
              <Select
                id="operating-hours-unit"
                value={form.reservationUnitMinutes}
                disabled={!editable || unitLocked}
                onChange={(event) => {
                  updateForm({ reservationUnitMinutes: Number(event.target.value) })
                }}
              >
                {unitOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {unitLocked && view.blockNotice ? (
                <p className="mt-1 text-xs text-slateish-500">{view.blockNotice}</p>
              ) : null}
            </div>
            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="operating-hours-max"
              >
                최대 예약 시간(분)
              </label>
              <Input
                id="operating-hours-max"
                inputMode="numeric"
                disabled={!editable}
                value={form.maxReservationMinutes}
                aria-invalid={Boolean(errors.maxReservationMinutes)}
                onChange={(event) => {
                  updateForm({ maxReservationMinutes: event.target.value })
                }}
              />
              {errors.maxReservationMinutes ? (
                <p className="mt-1 text-xs text-danger-500">{errors.maxReservationMinutes}</p>
              ) : null}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="운영시간"
            action={
              <label className="flex items-center gap-2 text-sm text-ink-700">
                요일별 운영시간 다르게 설정
                <Switch
                  checked={form.differsByDay}
                  disabled={!editable}
                  onChange={handleToggleDiffersByDay}
                  aria-label="요일별 운영시간 다르게 설정"
                />
              </label>
            }
          />
          <CardBody className="space-y-4">
            {!form.differsByDay ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    className="mb-1.5 block text-sm font-medium text-ink-700"
                    htmlFor="operating-hours-uniform-start"
                  >
                    시작
                  </label>
                  <Select
                    id="operating-hours-uniform-start"
                    disabled={!editable}
                    value={form.uniformStartTime}
                    onChange={(event) => {
                      updateForm({ uniformStartTime: event.target.value })
                    }}
                  >
                    {TIME_OF_DAY_OPTIONS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label
                    className="mb-1.5 block text-sm font-medium text-ink-700"
                    htmlFor="operating-hours-uniform-end"
                  >
                    종료
                  </label>
                  <Select
                    id="operating-hours-uniform-end"
                    disabled={!editable}
                    value={form.uniformEndTime}
                    onChange={(event) => {
                      updateForm({ uniformEndTime: event.target.value })
                    }}
                  >
                    {TIME_OF_DAY_OPTIONS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </Select>
                </div>
                {errors.uniformTime ? (
                  <p className="text-xs text-danger-500 sm:col-span-2">{errors.uniformTime}</p>
                ) : null}
              </div>
            ) : (
              <div className="space-y-3">
                {errors.daysGeneral ? (
                  <p className="text-xs text-danger-500">{errors.daysGeneral}</p>
                ) : null}
                {form.days.map((day) => (
                  <div
                    key={day.dayOfWeek}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-slateish-200 px-3 py-2.5"
                  >
                    <label className="flex w-16 items-center gap-2 text-sm font-medium text-ink-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slateish-300 text-brand-500 focus:ring-brand-500"
                        checked={day.active}
                        disabled={!editable}
                        onChange={(event) => {
                          updateDay(day.dayOfWeek, { active: event.target.checked })
                        }}
                      />
                      {day.dayOfWeekLabel}
                    </label>
                    {day.active ? (
                      <>
                        <Select
                          aria-label={`${day.dayOfWeekLabel}요일 시작`}
                          className="w-28"
                          disabled={!editable}
                          value={day.startTime}
                          onChange={(event) => {
                            updateDay(day.dayOfWeek, { startTime: event.target.value })
                          }}
                        >
                          {TIME_OF_DAY_OPTIONS.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </Select>
                        <span className="text-slateish-400">~</span>
                        <Select
                          aria-label={`${day.dayOfWeekLabel}요일 종료`}
                          className="w-28"
                          disabled={!editable}
                          value={day.endTime}
                          onChange={(event) => {
                            updateDay(day.dayOfWeek, { endTime: event.target.value })
                          }}
                        >
                          {TIME_OF_DAY_OPTIONS.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </Select>
                      </>
                    ) : (
                      <span className="text-xs text-slateish-500">휴무</span>
                    )}
                    {errors.dayErrors[day.dayOfWeek] ? (
                      <p className="w-full text-xs text-danger-500">
                        {errors.dayErrors[day.dayOfWeek]}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              navigate(`/venues/${venueId}/courts`)
            }}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={!editable || saveMutation.isPending}
            onClick={handleSaveClick}
          >
            변경사항 저장
          </Button>
        </div>
      </div>

      {confirming ? (
        <CourtOperatingHourSaveConfirmModal
          upcomingReservationCount={view.upcomingReservationCount}
          isPending={saveMutation.isPending}
          onCancel={() => {
            setConfirming(false)
          }}
          onConfirm={() => {
            void handleConfirmSave()
          }}
        />
      ) : null}
    </>
  )
}
